import { useState, useCallback, useEffect } from 'react';
// ExcelJS loaded lazily on first export to keep initial bundle small
import { jsPDF } from 'jspdf';
import { buildRevenueBlocks, blockRowsForExport } from '../utils/revenueBlocks';
import RevenueBlockTable from './ui/RevenueBlockTable';
import autoTable from 'jspdf-autotable';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, PAYMENT_MODES } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';

// --- Date helpers ---
const todayInput = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${year}-${month}-${day}`; // YYYY-MM-DD (local date)
};

const MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinalSuffix = (n) => { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const formatDateForHeader = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinalSuffix(d)} ${MONTHS_LONG[m-1]} ${y}`;
};

const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};

const toInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m, y] = ddmmyyyy.split('-');
  return `${y}-${m}-${d}`;
};


const fmt = (n) => (n || 0).toLocaleString('en-IN');
const fmtAmount = (n) => `₹${fmt(n)}`;

const CRUISE_ORDER_FALLBACK = ['Sunset Cruise', 'Sundown Cruise', 'Dinner Cruise'];
const DINNER_CRUISE = 'Dinner Cruise';

const PAYMENT_MODE_COLS = {
  'Razorpay':      'razorpay_amount',
  'Cash':          'cash_amount',
  'ICICI POS':     'icici_amount',
  'Bank Transfer': 'bank_transfer_amount',
  'GPay':          'gpay_amount',
};

// --- Styles ---
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const sectionLabel = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNumStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
const tfootStyle = { padding: '9px 12px', fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand)', textAlign: 'right', background: 'var(--color-sunken)', borderTop: '1px solid var(--color-border-strong)', fontVariantNumeric: 'tabular-nums' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 10 };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };

// One partner table (a cruise group). Hidden entirely when it has no rows.
function PartnerTable({ title, rows }) {
  if (rows.length === 0) return null;
  const T = rows.reduce((t, r) => ({ b: t.b + r.bookings, p: t.p + r.pax, g: t.g + r.gross, x: t.x + r.tac }), { b: 0, p: 0, g: 0, x: 0 });
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-faint)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Partner Report — {title}</div>
      <div style={tableWrap}>
        <table className="data-table" style={tblStyle}>
          <thead>
            <tr>
              {['Partner', 'Bookings', 'Pax', 'Gross', 'TAC'].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h === 'Partner' ? 'left' : 'right' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{r.name}</td>
                <td style={tdNumStyle}>{r.bookings}</td>
                <td style={tdNumStyle}>{r.pax}</td>
                <td style={tdNumStyle}>{fmtAmount(r.gross)}</td>
                <td style={{ ...tdNumStyle, color: '#92400e', fontWeight: 700 }}>{fmtAmount(r.tac)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }}>TOTAL</td>
              <td style={tfootStyle}>{T.b}</td>
              <td style={tfootStyle}>{T.p}</td>
              <td style={tfootStyle}>{fmtAmount(T.g)}</td>
              <td style={{ ...tfootStyle, color: '#92400e' }}>{fmtAmount(T.x)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function SectionTitle({ title }) {
  return <div style={sectionLabel}>{title}</div>;
}

// Every DD-MM-YYYY date from fromISO to toISO inclusive
const datesBetween = (fromISO, toISO) => {
  const out = [];
  const cur = new Date(fromISO + 'T00:00:00');
  const end = new Date(toISO + 'T00:00:00');
  while (cur <= end) {
    out.push(`${String(cur.getDate()).padStart(2, '0')}-${String(cur.getMonth() + 1).padStart(2, '0')}-${cur.getFullYear()}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

// Days since the cruise date for a Balance booking (DD-MM-YYYY) — 0 = today
const balanceDaysPending = (dateStr) => {
  const [d, m, yr] = (dateStr || '').split('-').map(Number);
  const cruiseDate = new Date(yr, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - cruiseDate) / 86400000));
};
const balanceDaysLabel = (n) => (n === 0 ? 'Today' : `${n}d`);

export default function Reports({ onNavigate, initialDate }) {
  // Use today's local date unless an explicit date was navigated from the Tracker
  const initDate = (initialDate && initialDate !== '') ? toInputDate(initialDate) : todayInput();

  const { settings: cruiseSettings } = useCruiseSettings();
  const CRUISE_ORDER = cruiseSettings.length > 0 ? cruiseSettings.map(s => s.cruise_name) : CRUISE_ORDER_FALLBACK;

  const [singleDate, setSingleDate] = useState(initDate);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  // Daily adjustments state
  const [adjustments, setAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({ date: initDate, description: '', amount: '' });
  const [balanceBookings, setBalanceBookings] = useState([]);
  const [adjError, setAdjError] = useState('');

  const effectiveFrom = singleDate;
  const effectiveTo = singleDate;

  const changeSingleDay = (delta) => {
    const d = new Date(singleDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setSingleDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  useEffect(() => {
    setAdjForm(f => ({ ...f, date: singleDate }));
  }, [singleDate]);

  // Balance (unpaid) — bookings on the report date(s) still carrying an unpaid remainder,
  // whatever their status: the "Balance (unpaid)" line of the revenue block, itemised.
  // Real-time so a payment recorded in the tracker drops off the list at once.
  const fetchBalanceBookings = useCallback(async () => {
    if (!effectiveFrom || !effectiveTo || effectiveFrom > effectiveTo) { setBalanceBookings([]); return; }
    const { data } = await supabase
      .from('bookings')
      .select('id, booking_name, partner_name, date, cruise_type, deck, adults, total_amount, tac, payment_status, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount')
      .in('date', datesBetween(effectiveFrom, effectiveTo))
      .not('payment_status', 'in', '("FOC","Cancelled")')
      .limit(2000);
    const rows = (data || [])
      .map(b => {
        const paid = PAYMENT_MODES.reduce((s, m) => s + (Number(b[PAYMENT_MODE_COLS[m]]) || 0), 0);
        const net = (Number(b.total_amount) || 0) - (Number(b.tac) || 0);
        return { ...b, net, paid, remaining: net - paid };
      })
      .filter(b => b.remaining > 0.5)
      .sort((a, b) => a.date.split('-').reverse().join('').localeCompare(b.date.split('-').reverse().join('')) || b.remaining - a.remaining);
    setBalanceBookings(rows);
  }, [effectiveFrom, effectiveTo]);

  useEffect(() => {
    fetchBalanceBookings();
    const sub = supabase
      .channel('reports-balance-payments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, fetchBalanceBookings)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchBalanceBookings]);

  const loadAdjustments = useCallback(async (from, to) => {
    const { data, error } = await supabase
      .from('daily_adjustments')
      .select('*')
      .gte('date', from)
      .lte('date', to)
      .order('date', { ascending: true });
    if (!error) setAdjustments(data || []);
  }, []);

  useEffect(() => {
    if (effectiveFrom && effectiveTo && effectiveFrom <= effectiveTo) {
      loadAdjustments(effectiveFrom, effectiveTo);
    }
  }, [effectiveFrom, effectiveTo, loadAdjustments]);

  const generateReport = useCallback(async () => {
    if (!effectiveFrom || !effectiveTo || effectiveFrom > effectiveTo) return;
    setLoading(true);
    setReport(null);

    try {
      const COLS = 'id, adults, children, tour_leaders, total_amount, tac, net_total, payment_status, payment_mode, partner_name, cruise_type, deck, booking_name, guest_phone, date, notes, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount, is_refunded, refund_amount, refund_mode, refund_date';

      // DD-MM-YYYY date array for the entire range
      const dateArray = datesBetween(effectiveFrom, effectiveTo);

      // Fetch each date in parallel — guarantees all bookings regardless of total count
      const bookingResults = await Promise.all(
        dateArray.map(date =>
          supabase.from('bookings').select(COLS).eq('date', date).limit(500)
        )
      );
      const allBookings = bookingResults.flatMap(r => r.data || []);

      const bookingIds = allBookings.map(b => b.id);

      // Chunk to avoid PostgREST URL length limits
      let trackerEntries = [];
      if (bookingIds.length > 0) {
        const CHUNK = 100;
        const chunks = [];
        for (let i = 0; i < bookingIds.length; i += CHUNK) chunks.push(bookingIds.slice(i, i + CHUNK));
        const teResults = await Promise.all(
          chunks.map(ids =>
            supabase.from('tracker_entries')
              .select('booking_id, driver_commission')
              .in('booking_id', ids)
              .limit(500)
          )
        );
        trackerEntries = teResults.flatMap(r => r.data || []);
      }

      const { data: adjData } = await supabase
        .from('daily_adjustments')
        .select('*')
        .gte('date', effectiveFrom)
        .lte('date', effectiveTo)
        .order('date', { ascending: true });
      setAdjustments(adjData || []);

      const pax = (b) => (b.adults || 0) + (b.children || 0);
      // revenueActive: non-FOC, non-Cancelled — used for gross/TAC stats and net revenue (billed side)
      // modeActive: non-FOC — the RECEIPTS row set. Payment-mode columns record money actually
      //   received (net of TAC at source, by convention), so Net Collection, the payment-mode
      //   breakdown, cash accounting AND refunds are all drawn from this one population. Any
      //   status (Confirmed, Pending, Balance, Cancelled) contributes exactly what it received;
      //   a booking with nothing received contributes nothing and has no TAC deducted.
      // boardingActive: confirmed/pending/balance (exclude Cancelled + FOC) — for pax/partner counts
      const revenueActive = allBookings.filter(b => b.payment_status !== 'FOC' && b.payment_status !== 'Cancelled');
      const modeActive = allBookings.filter(b => b.payment_status !== 'FOC');
      const boardingActive = allBookings.filter(b => !['Cancelled', 'FOC'].includes(b.payment_status));

      // --- Pax Summary ---
      const paxSummaryMap = {};
      boardingActive.forEach(b => {
        const key = `${b.cruise_type}||${b.deck}`;
        if (!paxSummaryMap[key]) paxSummaryMap[key] = { cruise: b.cruise_type, deck: b.deck, bookings: 0, adults: 0, children: 0, tl: 0, pax: 0 };
        paxSummaryMap[key].bookings += 1;
        paxSummaryMap[key].adults += b.adults || 0;
        paxSummaryMap[key].children += b.children || 0;
        paxSummaryMap[key].tl += b.tour_leaders || 0;
        paxSummaryMap[key].pax += pax(b);
      });
      const paxSummary = Object.values(paxSummaryMap).sort((a, b) => {
        const ci = CRUISE_ORDER.indexOf(a.cruise) - CRUISE_ORDER.indexOf(b.cruise);
        return ci !== 0 ? ci : a.deck.localeCompare(b.deck);
      });

      // --- Revenue summary ---
      const gross = revenueActive.reduce((s, b) => s + (b.total_amount || 0), 0);
      const totalTAC = revenueActive.reduce((s, b) => s + (b.tac || 0), 0);
      const netRevenue = revenueActive.reduce((s, b) => s + (b.net_total || 0), 0);

      // Payment mode breakdown — direct column sums only, no fallback
      const byMode = {};
      PAYMENT_MODES.forEach(m => { byMode[m] = 0; });
      modeActive.forEach(b => {
        PAYMENT_MODES.forEach(m => { byMode[m] += b[PAYMENT_MODE_COLS[m]] || 0; });
      });
      // Payments Received — every rupee recorded in a mode column on the receipts row set
      const receivedTotal = PAYMENT_MODES.reduce((s, m) => s + (byMode[m] || 0), 0);

      // Cash reconciliation — use cash_amount directly, not the net-mode breakdown
      const cashCollected = modeActive.reduce((s, b) => s + (Number(b.cash_amount) || 0), 0);
      const driverCommission = trackerEntries.reduce((s, te) => s + (Number(te.driver_commission) || 0), 0);
      const adjItems = adjData || [];
      const adjNet = adjItems.reduce((s, a) => s + (Number(a.amount) || 0), 0);

      // Block 1 lines (see utils/revenueBlocks). Balance (unpaid) is the remainder still
      // owed on billed bookings — (total − tac) − paid — whatever their status, so a
      // part-paid Balance booking and an unpaid Pending one both count what is actually
      // outstanding. Cancelled bookings are outside Gross but their receipts are real.
      const paidOf = (b) => PAYMENT_MODES.reduce((s, m) => s + (Number(b[PAYMENT_MODE_COLS[m]]) || 0), 0);
      const unpaidRemaining = revenueActive.reduce((s, b) => s + ((Number(b.total_amount) || 0) - (Number(b.tac) || 0) - paidOf(b)), 0);
      const cancelledReceipts = allBookings.filter(b => b.payment_status === 'Cancelled').reduce((s, b) => s + paidOf(b), 0);

      // Refunds — same receipts row set as the payments, so payment and refund are
      // counted together (or neither)
      const refundedBookings = modeActive.filter(b => b.is_refunded && Number(b.refund_amount) > 0);
      const totalRefunds = refundedBookings.reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);
      const cashRefunds = refundedBookings.filter(b => b.refund_mode === 'Cash').reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);

      // Subtract refunds from each non-Cash payment mode total; a refund whose mode is not
      // a known payment mode lands in Uncategorised so the mode rows still sum to the total
      let uncategorised = 0;
      refundedBookings.forEach(b => {
        if (b.refund_mode === 'Cash') return;
        if (b.refund_mode && byMode[b.refund_mode] !== undefined) {
          byMode[b.refund_mode] -= Number(b.refund_amount) || 0;
        } else {
          uncategorised -= Number(b.refund_amount) || 0;
        }
      });

      const cashInHand = cashCollected - driverCommission + adjNet - cashRefunds;
      // Net Collection = Payments Received − driver commission − refunds ± adjustments.
      // Receipts are recorded net of TAC at source, so TAC is not deducted again. Identical
      // by construction to Block 1 (Gross → Net Collection) in utils/revenueBlocks.
      const netCollection = receivedTotal - driverCommission + adjNet - totalRefunds;

      // --- Partner report, one table per cruise group ---
      // Sunset & Sundown share a table; Dinner has its own. A partner with both
      // appears in both, each row carrying only that group's bookings/pax/gross/TAC.
      const emptyGroup = () => ({ bookings: 0, pax: 0, gross: 0, tac: 0 });
      const partnerMap = {};
      boardingActive.forEach(b => {
        const name = b.partner_name || null;
        if (!name) return;
        if (!partnerMap[name]) partnerMap[name] = { name, sunset: emptyGroup(), dinner: emptyGroup() };
        const g = partnerMap[name][b.cruise_type === DINNER_CRUISE ? 'dinner' : 'sunset'];
        g.bookings += 1;
        g.pax += pax(b);
        g.gross += b.total_amount || 0;
        g.tac += b.tac || 0;
      });
      const partnerTable = (key) => Object.values(partnerMap)
        .filter(p => p[key].tac > 0)
        .map(p => ({ name: p.name, ...p[key] }))
        .sort((a, b) => b.tac - a.tac);
      const partnerSunset = partnerTable('sunset');
      const partnerDinner = partnerTable('dinner');
      // Combined TAC across both tables — reconciles with the "TAC at source" block line
      const partnerTACTotal = [...partnerSunset, ...partnerDinner].reduce((s, p) => s + p.tac, 0);

      // --- Bank transfer details --- (modeActive: cancelled bookings' transfers are real receipts)
      const bankTransfers = modeActive
        .filter(b => Number(b.bank_transfer_amount) > 0)
        .sort((a, b) => a.date.localeCompare(b.date));

      setReport({
        paxSummary, gross, totalTAC, netRevenue, netCollection, receivedTotal,
        byMode, uncategorised, unpaidRemaining, cancelledReceipts,
        cashCollected, driverCommission, adjItems, adjNet, cashInHand,
        refundedBookings, totalRefunds,
        partnerSunset, partnerDinner, partnerTACTotal, bankTransfers,
        fromDate: effectiveFrom, toDate: effectiveTo,
      });
    } finally {
      setLoading(false);
    }
  }, [effectiveFrom, effectiveTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate on mount and whenever dates change.
  // All three deps change in the same render so this fires exactly once per date change.
  useEffect(() => {
    if (effectiveFrom && effectiveTo && effectiveFrom <= effectiveTo) {
      generateReport();
    }
  }, [generateReport, effectiveFrom, effectiveTo]);

  // --- Adjustments CRUD ---
  const addAdjustment = async () => {
    setAdjError('');
    if (!adjForm.date || !adjForm.description.trim() || adjForm.amount === '') {
      setAdjError('All fields required');
      return;
    }
    const amount = Number(adjForm.amount);
    if (isNaN(amount)) { setAdjError('Amount must be a number'); return; }
    const { error } = await supabase.from('daily_adjustments').insert({ date: adjForm.date, description: adjForm.description.trim(), amount });
    if (error) { setAdjError('Save failed: ' + error.message); return; }
    setAdjForm({ date: singleDate, description: '', amount: '' });
    if (report) generateReport();
    else await loadAdjustments(effectiveFrom, effectiveTo);
  };

  const deleteAdjustment = async (id) => {
    await supabase.from('daily_adjustments').delete().eq('id', id);
    if (report) generateReport();
    else await loadAdjustments(effectiveFrom, effectiveTo);
  };

  // --- Export to Excel ---
  const exportExcel = async () => {
    if (!report) return;
    try {
      const { default: ExcelJS } = await import('exceljs');
      const wb = new ExcelJS.Workbook();

      const isSingleDay = report.fromDate === report.toDate;
      const fromDisp = toDisplayDate(report.fromDate);
      const toDisp = toDisplayDate(report.toDate);
      const reportTitle = isSingleDay
        ? `Alfresco Grand — Cruise Report — ${formatDateForHeader(report.fromDate)}`
        : `Alfresco Grand — Cruise Report — ${formatDateForHeader(report.fromDate)} to ${formatDateForHeader(report.toDate)}`;
      const filename = isSingleDay
        ? `Cruise_Report_${fromDisp}.xlsx`
        : `Cruise_Report_${fromDisp}_to_${toDisp}.xlsx`;

      // Amounts: plain ₹ prefix, no comma separators
      const rupee = (n) => `₹${Math.round(n || 0)}`;

      // Shared style constants
      const NAVY_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0A1628' } };
      const ALT_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F5F1' } };
      const THIN = { style: 'thin', color: { argb: 'FFD0C8B8' } };
      const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

      // Builds a styled sheet:
      //   Row 1  — report title (navy text, tall)
      //   Row 2  — blank spacer
      //   Row 3  — column headers (navy fill, white bold text, borders)
      //   Row 4+ — data rows (borders, alternating light fill)
      // Columns are auto-fitted to content and first 3 rows are frozen.
      const addSheet = (name, colHeaders, dataRows) => {
        const ws = wb.addWorksheet(name);

        // Row 1: Report title
        const r1 = ws.addRow([reportTitle]);
        r1.height = 24;
        r1.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF0A1628' } };

        // Row 2: Blank spacer
        const r2 = ws.addRow([]);
        r2.height = 8;

        // Row 3: Column headers — navy background, white bold
        const r3 = ws.addRow(colHeaders);
        r3.height = 18;
        for (let c = 1; c <= colHeaders.length; c++) {
          const cell = r3.getCell(c);
          cell.fill = NAVY_FILL;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
          cell.border = BORDER;
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        }

        // Data rows: borders + alternate row shading
        dataRows.forEach((values, idx) => {
          const row = ws.addRow(values);
          row.height = 15;
          const useFill = idx % 2 === 1;
          for (let c = 1; c <= colHeaders.length; c++) {
            const cell = row.getCell(c);
            cell.border = BORDER;
            cell.alignment = { vertical: 'middle' };
            if (useFill) cell.fill = ALT_FILL;
          }
        });

        // Auto-fit column widths from header + data content
        for (let c = 1; c <= colHeaders.length; c++) {
          let maxW = String(colHeaders[c - 1]).length;
          dataRows.forEach(row => {
            const len = String(row[c - 1] ?? '').length;
            if (len > maxW) maxW = len;
          });
          ws.getColumn(c).width = Math.min(maxW + 3, 55);
        }

        // Freeze rows 1-3 so column headers stay visible when scrolling
        ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 3, topLeftCell: 'A4' }];

        return ws;
      };

      // Sheet 1: Daily Summary
      const totalPax = report.paxSummary.reduce((s, r) => s + r.pax, 0);
      addSheet('Daily Summary', ['Item', 'Value'], [
        ['Report Period', isSingleDay ? fromDisp : `${fromDisp} to ${toDisp}`],
        ['Total Pax', totalPax],
        ['Gross Revenue', rupee(report.gross)],
        ['TAC at source', rupee(report.totalTAC)],
        ['Net Collection', rupee(report.netCollection)],
        ['Cash in Hand', rupee(report.cashInHand)],
        ['Balance (unpaid)', rupee(report.unpaidRemaining)],
      ]);

      // Sheet 2: Pax Summary
      const paxTotals = report.paxSummary.reduce(
        (s, r) => ({ b: s.b + r.bookings, a: s.a + r.adults, c: s.c + r.children, t: s.t + r.tl, p: s.p + r.pax }),
        { b: 0, a: 0, c: 0, t: 0, p: 0 }
      );
      addSheet('Pax Summary',
        ['Cruise', 'Deck', 'Bookings', 'Adults', 'Children', 'Tour Leaders', 'Total Pax'],
        [
          ...report.paxSummary.map(r => [r.cruise, r.deck, r.bookings, r.adults, r.children, r.tl, r.pax]),
          ['TOTAL', '', paxTotals.b, paxTotals.a, paxTotals.c, paxTotals.t, paxTotals.p],
        ]
      );

      // Sheet 3: Revenue Summary — the two rolling subtractions, running figure in column C
      const { block1, block2 } = buildRevenueBlocks(report);
      addSheet('Revenue Summary', ['Line', 'Amount', 'Running'], [
        ['— Gross Revenue to Net Collection —', '', ''],
        ...blockRowsForExport(block1, rupee),
        ['', '', ''],
        ['— Net Collection to Cash in Hand —', '', ''],
        ...blockRowsForExport(block2, rupee),
        ...(report.totalRefunds > 0 ? [['', '', ''], ['— Refunds —', '', ''], ...report.refundedBookings.map(b => [b.booking_name, `−${rupee(b.refund_amount)}`, b.refund_mode || '']), ['TOTAL REFUNDS', `−${rupee(report.totalRefunds)}`, '']] : []),
      ]);

      // Sheet 4: Partner Report — one block per cruise group, combined TAC at the foot
      const partnerRows = (title, rows) => rows.length === 0 ? [] : [
        [`— ${title} —`, '', '', '', ''],
        ...rows.map(r => [r.name, r.bookings, r.pax, rupee(r.gross), rupee(r.tac)]),
        ['TOTAL', rows.reduce((s, r) => s + r.bookings, 0), rows.reduce((s, r) => s + r.pax, 0), rupee(rows.reduce((s, r) => s + r.gross, 0)), rupee(rows.reduce((s, r) => s + r.tac, 0))],
        ['', '', '', '', ''],
      ];
      addSheet('Partner Report',
        ['Partner', 'Bookings', 'Pax', 'Gross Revenue', 'TAC'],
        (report.partnerSunset.length + report.partnerDinner.length) === 0
          ? [['No partner bookings for this date']]
          : [
              ...partnerRows('Sunset & Sundown', report.partnerSunset),
              ...partnerRows('Dinner', report.partnerDinner),
              ['TOTAL TAC AT SOURCE (all cruises)', '', '', '', rupee(report.partnerTACTotal)],
            ]
      );

      // Sheet 6: Bank Transfers
      const hasBT = report.bankTransfers.length > 0;
      addSheet('Bank Transfers',
        hasBT ? ['Date', 'Name', 'Partner', 'Cruise', 'Deck', 'Adults', 'Children', 'Amount', 'Notes'] : ['Note'],
        hasBT
          ? report.bankTransfers.map(b => [b.date, b.booking_name, b.partner_name || '', b.cruise_type, b.deck, b.adults, b.children || 0, rupee(b.bank_transfer_amount), b.notes || ''])
          : [['No bank transfers in this period']]
      );

      // Sheet 7: Adjustments
      const hasAdj = report.adjItems.length > 0;
      addSheet('Adjustments',
        hasAdj ? ['Date', 'Description', 'Amount'] : ['Note'],
        hasAdj
          ? [...report.adjItems.map(a => [a.date, a.description, rupee(a.amount)]), ['', 'NET', rupee(report.adjNet)]]
          : [['No adjustments in this period']]
      );

      // Sheet 8: Balance (unpaid) — this report's dates only, mirrors the on-screen section
      const hasBal = balanceBookings.length > 0;
      addSheet('Balance (unpaid)',
        hasBal ? ['Date', 'Booking Name', 'Partner', 'Cruise', 'Deck', 'Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'] : ['Note'],
        hasBal
          ? [
              ...balanceBookings.map(b => [b.date, b.booking_name, b.partner_name || '', b.cruise_type, b.deck || '', b.adults || 0, rupee(b.net), b.paid ? rupee(b.paid) : '', rupee(b.remaining), balanceDaysLabel(balanceDaysPending(b.date))]),
              ['TOTAL UNPAID', '', '', '', '', '', '', '', rupee(balanceBookings.reduce((s, b) => s + b.remaining, 0)), ''],
            ]
          : [['No unpaid balances on this date']]
      );

      // Trigger browser download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  };

  // --- Export to PDF ---
  const exportPDF = () => {
    if (!report) return;

    // ── Setup ───────────────────────────────────────────────────────────────
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const PW = 210, PH = 297, ML = 15, MR = 15, MT = 15, MB = 15;
    const W = PW - ML - MR; // 180mm usable width
    // Type scale (pt). Was 6.5 head / 7–8 body / 8 section heads — too small to
    // read on paper. Widths below are balanced for these sizes; long cells
    // (partner names) wrap, rows never split.
    const F_SEC = 9.5, F_HEAD = 7.5, F_BODY = 8.5, F_BIG = 9.5, F_NOTE = 8.5;

    const CNAV = [10, 22, 40];
    const CG66 = [102, 102, 102];
    const CG99 = [153, 153, 153];
    const CGcc = [204, 204, 204];
    const CBLK = [0, 0, 0];
    const C444 = [68, 68, 68];

    // No comma separators, Rs. prefix
    const r$ = (n) => 'Rs.' + Math.round(Math.abs(n || 0));

    // ── Header ──────────────────────────────────────────────────────────────
    let y = MT;

    // Large date
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...CNAV);
    doc.text(formatDateForHeader(report.fromDate), ML, y);

    // Generated timestamp — right aligned, same baseline as date
    const now = new Date();
    const MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const hh = now.getHours(), mm = now.getMinutes();
    const genStr = `Generated: ${now.getDate()} ${MONS[now.getMonth()]} ${now.getFullYear()}, ${hh % 12 || 12}:${String(mm).padStart(2,'0')} ${hh >= 12 ? 'PM' : 'AM'}`;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...CG99);
    doc.text(genStr, PW - MR, y, { align: 'right' });

    y += 5.5;

    // Subtitle
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...CG66);
    doc.setCharSpace(0.5);
    doc.text('DAILY OPERATIONS REPORT', ML, y);
    doc.setCharSpace(0);

    y += 4.5;

    // Full-width navy rule (1.5pt)
    doc.setDrawColor(...CNAV);
    doc.setLineWidth(0.53);
    doc.line(ML, y, PW - MR, y);

    y += 6;

    // ── Helpers ─────────────────────────────────────────────────────────────

    // Draw a bottom border on a cell (called from didDrawCell)
    const hLine = (data, color, lw, edge = 'bottom') => {
      const { x, y: cy, width, height } = data.cell;
      doc.setDrawColor(...color);
      doc.setLineWidth(lw);
      doc.line(x, edge === 'top' ? cy : cy + height, x + width, edge === 'top' ? cy : cy + height);
    };

    // Section heading + grey underline
    const secHead = (title) => {
      if (y > PH - MB - 25) { doc.addPage(); y = MT; }
      doc.setFontSize(F_SEC);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...CNAV);
      doc.setCharSpace(0.4);
      doc.text(title, ML, y);
      doc.setCharSpace(0);
      y += 3;
      doc.setDrawColor(...CGcc);
      doc.setLineWidth(0.18);
      doc.line(ML, y, PW - MR, y);
      y += 3;
    };

    // Base config shared by all tables
    const tbl = (startY) => ({
      startY,
      margin: { left: ML, right: MR, bottom: MB + 8 },
      styles: { overflow: 'linebreak', lineWidth: 0, fillColor: [255, 255, 255] },
      tableLineWidth: 0,
      theme: 'plain',
      rowPageBreak: 'avoid',   // a multi-line row moves whole to the next page
      showHead: 'everyPage',   // column headers repeat on continuation pages
    });

    // Muted one-line note for sections that are on screen but have no rows
    const noneLine = (text) => {
      doc.setFontSize(F_NOTE);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...CG99);
      doc.text(text, ML, y + 1);
      y += 6;
    };

    // Generic list table: grey column headers, hairline rows, bold navy total row(s) with a top rule.
    // cols: [{ w: fraction of W, align }], totalRows: Set of body row indices to style as totals
    const listTable = ({ head, body, cols, totalRows = new Set(), totalColor = CNAV }) => {
      autoTable(doc, {
        ...tbl(y),
        head: [head],
        body,
        headStyles: { fillColor: [255,255,255], textColor: CG66, fontStyle: 'bold', fontSize: F_HEAD, cellPadding: { top: 1.2, right: 1.5, bottom: 1.2, left: 1.5 } },
        bodyStyles: { fillColor: [255,255,255], textColor: CBLK, fontSize: F_BODY, cellPadding: { top: 0.9, right: 1.5, bottom: 0.9, left: 1.5 } },
        columnStyles: Object.fromEntries(cols.map((c, i) => [i, { cellWidth: W * c.w, halign: c.align || 'left' }])),
        didParseCell: (data) => {
          if (data.section === 'head' && cols[data.column.index]?.align === 'right') {
            data.cell.styles.halign = 'right';
          }
          if (data.section === 'body' && totalRows.has(data.row.index)) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = totalColor;
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'head') {
            hLine(data, CGcc, 0.25, 'bottom');
          } else if (data.section === 'body') {
            if (totalRows.has(data.row.index)) {
              hLine(data, CNAV, 0.35, 'top');
            } else {
              hLine(data, CGcc, 0.1, 'bottom');
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 5;
    };

    // ── SECTION 1 — PAX SUMMARY ─────────────────────────────────────────────
    secHead('PAX SUMMARY');

    const PT = report.paxSummary.reduce(
      (s, r) => ({ a: s.a + r.adults, c: s.c + r.children, p: s.p + r.pax }),
      { a: 0, c: 0, p: 0 }
    );
    const PT_IDX = report.paxSummary.length;

    autoTable(doc, {
      ...tbl(y),
      head: [['CRUISE', 'DECK', 'ADULTS', 'CHILDREN', 'TOTAL']],
      body: [
        ...report.paxSummary.map(r => [r.cruise, r.deck, r.adults, r.children, r.pax]),
        ['TOTAL', '', PT.a, PT.c, PT.p],
      ],
      headStyles: { fillColor: [255,255,255], textColor: CG66, fontStyle: 'bold', fontSize: F_HEAD, cellPadding: { top: 1.5, right: 2, bottom: 1.5, left: 2 } },
      bodyStyles: { fillColor: [255,255,255], textColor: CBLK, fontSize: F_BIG, cellPadding: { top: 1, right: 2, bottom: 1, left: 2 } },
      columnStyles: {
        0: { cellWidth: W * 0.28, halign: 'left'  },
        1: { cellWidth: W * 0.15, halign: 'left'  },
        2: { cellWidth: W * 0.19, halign: 'right' },
        3: { cellWidth: W * 0.19, halign: 'right' },
        4: { cellWidth: W * 0.19, halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index >= 2) {
          data.cell.styles.halign = 'right';
        }
        if (data.section === 'body' && data.row.index === PT_IDX) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = CNAV;
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'head') {
          hLine(data, CGcc, 0.25, 'bottom');
        } else if (data.section === 'body') {
          if (data.row.index === PT_IDX) {
            hLine(data, CNAV, 0.35, 'top');
          } else {
            hLine(data, CGcc, 0.1, 'bottom');
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 5;

    // ── SECTION 2 — REVENUE SUMMARY ─────────────────────────────────────────
    secHead('REVENUE SUMMARY');

    // Two rolling subtractions (utils/revenueBlocks): Line | Amount | Running.
    // Same rows as the screen and the Excel sheet.
    const { block1: pdfBlock1, block2: pdfBlock2 } = buildRevenueBlocks(report);
    const blockTable = (subLabel, rows) => {
      doc.setFontSize(F_HEAD + 0.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...CG66);
      doc.setCharSpace(0.3);
      doc.text(subLabel, ML, y);
      doc.setCharSpace(0);
      y += 2.5;
      // Helvetica has no U+2212 — hyphen-minus in the PDF. The amount column carries
      // the sign; a wide right pad on it keeps Amount and Running visibly apart.
      const body = blockRowsForExport(rows, r$, { minus: '-' });
      const AMT_PAD = { top: 0.75, right: 10, bottom: 0.75, left: 2 };
      autoTable(doc, {
        ...tbl(y),
        head: [['', 'AMOUNT', 'RUNNING']],
        body,
        headStyles: { fillColor: [255,255,255], textColor: CG66, fontSize: F_HEAD, fontStyle: 'bold', cellPadding: { top: 0.5, right: 2, bottom: 0.5, left: 2 } },
        bodyStyles: { fillColor: [255,255,255], textColor: CBLK, fontSize: F_BIG, cellPadding: { top: 1, right: 2, bottom: 1, left: 2 } },
        columnStyles: {
          0: { cellWidth: W - 100, halign: 'left'  },
          1: { cellWidth: 50,      halign: 'right', cellPadding: AMT_PAD },
          2: { cellWidth: 50,      halign: 'right' },
        },
        didParseCell: (data) => {
          if (data.section === 'head' && data.column.index >= 1) data.cell.styles.halign = 'right';
          if (data.section === 'head' && data.column.index === 1) data.cell.styles.cellPadding = { top: 0.5, right: 10, bottom: 0.5, left: 2 };
          if (data.section !== 'body') return;
          const row = rows[data.row.index];
          if (!row) return;
          if (data.column.index === 1) data.cell.styles.cellPadding = AMT_PAD;
          if (row.kind === 'line') {
            if (data.column.index < 2) data.cell.styles.textColor = C444;
            if (data.column.index === 0) data.cell.styles.cellPadding = { top: 0.75, right: 2, bottom: 0.75, left: 6 };
            if (data.column.index === 2) data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = CNAV;
            if (row.kind === 'result') data.cell.styles.fontSize = F_BIG + 1;
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'head') { hLine(data, CGcc, 0.25, 'bottom'); return; }
          if (data.section !== 'body') return;
          const row = rows[data.row.index];
          if (row?.kind === 'result') hLine(data, CNAV, 0.35, 'top');
          else hLine(data, CGcc, 0.1, 'bottom');
        },
      });
      y = doc.lastAutoTable.finalY + 5;
    };
    blockTable('GROSS REVENUE TO NET COLLECTION', pdfBlock1);
    blockTable('NET COLLECTION TO CASH IN HAND', pdfBlock2);

    // ── REFUNDS (itemised — shown on screen only when there are refunds) ─────
    if (report.refundedBookings.length > 0) {
      secHead('REFUNDS');
      listTable({
        head: ['BOOKING', 'DATE', 'MODE', 'AMOUNT'],
        body: [
          ...report.refundedBookings.map(b => [b.booking_name, b.refund_date || b.date, b.refund_mode || '—', `-${r$(b.refund_amount)}`]),
          ['TOTAL REFUNDS', '', '', `-${r$(report.totalRefunds)}`],
        ],
        cols: [{ w: 0.45 }, { w: 0.20 }, { w: 0.15 }, { w: 0.20, align: 'right' }],
        totalRows: new Set([report.refundedBookings.length]),
      });
    }

    // ── SECTION 3 — PARTNER REPORT (one table per cruise group) ─────────────
    {
      const partnerTable = (title, rows) => {
        if (rows.length === 0) return;
        secHead(title);
        const T = rows.reduce((t, r) => ({ b: t.b + r.bookings, p: t.p + r.pax, g: t.g + r.gross, x: t.x + r.tac }), { b: 0, p: 0, g: 0, x: 0 });
        listTable({
          head: ['PARTNER', 'BOOKINGS', 'PAX', 'GROSS', 'TAC'],
          body: [
            ...rows.map(r => [r.name, r.bookings, r.pax, r$(r.gross), r$(r.tac)]),
            ['TOTAL', T.b, T.p, r$(T.g), r$(T.x)],
          ],
          cols: [{ w: 0.44 }, { w: 0.14, align: 'right' }, { w: 0.12, align: 'right' }, { w: 0.15, align: 'right' }, { w: 0.15, align: 'right' }],
          totalRows: new Set([rows.length]),
        });
      };
      partnerTable('PARTNER REPORT — SUNSET & SUNDOWN', report.partnerSunset);
      partnerTable('PARTNER REPORT — DINNER', report.partnerDinner);
      if (report.partnerSunset.length + report.partnerDinner.length === 0) {
        secHead('PARTNER REPORT');
        noneLine('No partner bookings for this date');
      } else {
        // Combined TAC — the figure that reconciles with the revenue block's "TAC at source"
        doc.setFontSize(F_BODY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...CNAV);
        doc.text('Total TAC at source, all cruises', ML + 2, y);
        doc.text(r$(report.partnerTACTotal), PW - MR - 2, y, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        y += 6;
      }
    }

    // ── SECTION 4 — BANK TRANSFER DETAILS ───────────────────────────────────
    secHead('BANK TRANSFER DETAILS');
    if (report.bankTransfers.length === 0) {
      noneLine('No bank transfers for this date');
    } else {

      const BT_NET = report.bankTransfers.reduce((s, b) => s + (Number(b.bank_transfer_amount) || 0), 0);
      const BT_IDX = report.bankTransfers.length;

      autoTable(doc, {
        ...tbl(y),
        head: [['NAME', 'PARTNER', 'CRUISE', 'DECK', 'ADULTS', 'CHILDREN', 'BANK TRANSFER', 'NOTES']],
        body: [
          ...report.bankTransfers.map(b => [
            b.booking_name, b.partner_name || '—', b.cruise_type, b.deck || '—',
            b.adults || 0, b.children || 0, r$(Number(b.bank_transfer_amount) || 0), b.notes || '—',
          ]),
          ['TOTAL', '', '', '', '', '', r$(BT_NET), ''],
        ],
        headStyles: { fillColor: [255,255,255], textColor: CG66, fontStyle: 'bold', fontSize: F_HEAD, cellPadding: { top: 1.2, right: 1, bottom: 1.2, left: 1 } },
        bodyStyles: { fillColor: [255,255,255], textColor: CBLK, fontSize: F_BODY, cellPadding: { top: 0.9, right: 1, bottom: 0.9, left: 1 } },
        columnStyles: {
          0: { cellWidth: W * 0.18, halign: 'left'  },
          1: { cellWidth: W * 0.16, halign: 'left'  },
          2: { cellWidth: W * 0.13, halign: 'left'  },
          3: { cellWidth: W * 0.10, halign: 'left'  },
          4: { cellWidth: W * 0.07, halign: 'right' },
          5: { cellWidth: W * 0.08, halign: 'right' },
          6: { cellWidth: W * 0.12, halign: 'right' },
          7: { cellWidth: W * 0.16, halign: 'left'  },
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.index === BT_IDX) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = CNAV;
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'head') {
            hLine(data, CGcc, 0.25, 'bottom');
          } else if (data.section === 'body') {
            if (data.row.index === BT_IDX) {
              hLine(data, CNAV, 0.35, 'top');
            } else {
              hLine(data, CGcc, 0.1, 'bottom');
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    // ── DAILY ADJUSTMENTS (itemised) ────────────────────────────────────────
    secHead('DAILY ADJUSTMENTS');
    if (report.adjItems.length === 0) {
      noneLine('No adjustments for this date range');
    } else {
      const signed = (n) => (Number(n) >= 0 ? '+' : '-') + r$(Math.abs(Number(n)));
      listTable({
        head: ['DATE', 'DESCRIPTION', 'AMOUNT'],
        body: [
          ...report.adjItems.map(a => [a.date, a.description, signed(a.amount)]),
          ['NET', '', signed(report.adjNet)],
        ],
        cols: [{ w: 0.18 }, { w: 0.62 }, { w: 0.20, align: 'right' }],
        totalRows: new Set([report.adjItems.length]),
      });
    }

    // ── SECTION 5 — BALANCE (UNPAID) ────────────────────────────────────────
    // Mirrors the on-screen section: this report's dates only, unpaid remainder > 0.
    if (balanceBookings.length > 0) {
      secHead('BALANCE (UNPAID)');
      const BAL_NET = balanceBookings.reduce((s, b) => s + b.remaining, 0);
      const BAL_IDX = balanceBookings.length;

      autoTable(doc, {
        ...tbl(y),
        head: [['DATE', 'BOOKING NAME', 'PARTNER', 'CRUISE', 'DECK', 'ADULTS', 'NET', 'PAID', 'UNPAID', 'DAYS']],
        body: [
          ...balanceBookings.map(b => [
            b.date, b.booking_name, b.partner_name || '', b.cruise_type, b.deck || '—',
            b.adults || 0, r$(b.net), b.paid ? r$(b.paid) : '', r$(b.remaining), balanceDaysLabel(balanceDaysPending(b.date)),
          ]),
          [{ content: 'TOTAL UNPAID', colSpan: 8 }, r$(BAL_NET), ''],
        ],
        headStyles: { fillColor: [255,255,255], textColor: CG66, fontStyle: 'bold', fontSize: F_HEAD, cellPadding: { top: 1.2, right: 1, bottom: 1.2, left: 1 } },
        bodyStyles: { fillColor: [255,255,255], textColor: CBLK, fontSize: F_BODY, cellPadding: { top: 0.9, right: 1, bottom: 0.9, left: 1 } },
        pageBreak: 'avoid',   // short daily list — move whole to the next page rather than split
        columnStyles: {
          0: { cellWidth: W * 0.10, halign: 'left'  },   // 18mm: "18-09-2026" needs ~17.6 at 8.5pt
          1: { cellWidth: W * 0.14, halign: 'left'  },   // guest name wraps if long
          2: { cellWidth: W * 0.17, halign: 'left'  },   // partner name wraps (Brahmaputra… takes 2 lines)
          3: { cellWidth: W * 0.12, halign: 'left'  },
          4: { cellWidth: W * 0.09, halign: 'left'  },   // "Middle Deck" wraps to two lines
          5: { cellWidth: W * 0.07, halign: 'right' },   // 12.6mm: "ADULTS" header needs ~11.5
          6: { cellWidth: W * 0.09, halign: 'right' },   // 16.2mm: "Rs.13680" needs ~14.5
          7: { cellWidth: W * 0.08, halign: 'right' },
          8: { cellWidth: W * 0.09, halign: 'right' },
          9: { cellWidth: W * 0.05, halign: 'right' },
        },
        didParseCell: (data) => {
          if (data.section === 'head' && data.column.index >= 5) data.cell.styles.halign = 'right';
          if (data.section === 'body' && data.row.index === BAL_IDX) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = CNAV;
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'head') {
            hLine(data, CGcc, 0.25, 'bottom');
          } else if (data.section === 'body') {
            if (data.row.index === BAL_IDX) {
              hLine(data, CNAV, 0.35, 'top');
            } else {
              hLine(data, CGcc, 0.1, 'bottom');
            }
          }
        },
      });
    }

    // ── Footer on every page ─────────────────────────────────────────────────
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      const fy = PH - MB + 2;
      doc.setDrawColor(...CGcc);
      doc.setLineWidth(0.18);
      doc.line(ML, fy - 3.5, PW - MR, fy - 3.5);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...CG99);
      doc.text('Alfresco Grand | Brahmaputra Cruise Pvt. Ltd.', ML, fy);
      doc.text(`Page ${p} of ${totalPages}`, PW - MR, fy, { align: 'right' });
    }

    doc.save(`QuickReport_${toDisplayDate(report.fromDate)}.pdf`);
  };

  const totalPaxSummary = report
    ? report.paxSummary.reduce((s, r) => ({ bookings: s.bookings + r.bookings, adults: s.adults + r.adults, children: s.children + r.children, tl: s.tl + r.tl, pax: s.pax + r.pax }), { bookings: 0, adults: 0, children: 0, tl: 0, pax: 0 })
    : null;

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Quick Reports</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
        Single-day financial and operational report.
      </p>

      {/* Date selector */}
      <div style={card}>
        <div style={sectionLabel}>Report Date</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => changeSingleDay(-1)} style={{ width: 36, height: 36, background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-label="Previous day">←</button>
            <input
              type="date"
              value={singleDate}
              onChange={e => setSingleDate(e.target.value)}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }}
            />
            <button onClick={() => changeSingleDay(1)} style={{ width: 36, height: 36, background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-label="Next day">→</button>
          </div>
          <button
            onClick={generateReport}
            disabled={loading || !singleDate}
            style={{ padding: '10px 24px', background: loading ? '#ccc' : NAVY, color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.88rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', alignSelf: 'center' }}
          >
            {loading ? 'Generating…' : 'Generate Report'}
          </button>
        </div>
        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: GOLD, marginTop: 8 }}>{formatDateForHeader(singleDate)}</div>
      </div>

      {report && (
        <>
          {/* Export buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <button onClick={exportExcel}
              style={{ padding: '9px 20px', background: '#166534', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
              Export Excel (.xlsx)
            </button>
            <button onClick={exportPDF}
              style={{ padding: '9px 20px', background: '#991b1b', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
              Export PDF
            </button>
          </div>

          {/* Pax Summary */}
          <div style={card}>
            <SectionTitle title="Pax Summary" />
            <div style={tableWrap}>
              <table className="data-table" style={tblStyle}>
                <thead>
                  <tr>
                    {['Cruise', 'Deck', 'Bookings', 'Adults', 'Children', 'TL', 'Total Pax'].map(h => (
                      <th key={h} style={{ ...thStyle, textAlign: ['Bookings','Adults','Children','TL','Total Pax'].includes(h) ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.paxSummary.map((r, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{r.cruise}</td>
                      <td style={tdStyle}>{r.deck}</td>
                      <td style={tdNumStyle}>{r.bookings}</td>
                      <td style={tdNumStyle}>{r.adults}</td>
                      <td style={tdNumStyle}>{r.children}</td>
                      <td style={tdNumStyle}>{r.tl}</td>
                      <td style={{ ...tdNumStyle, fontWeight: 800, color: NAVY }}>{r.pax}</td>
                    </tr>
                  ))}
                </tbody>
                {totalPaxSummary && (
                  <tfoot>
                    <tr>
                      <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={2}>TOTAL</td>
                      <td style={tfootStyle}>{totalPaxSummary.bookings}</td>
                      <td style={tfootStyle}>{totalPaxSummary.adults}</td>
                      <td style={tfootStyle}>{totalPaxSummary.children}</td>
                      <td style={tfootStyle}>{totalPaxSummary.tl}</td>
                      <td style={{ ...tfootStyle, color: GOLD, fontSize: '0.9rem' }}>{totalPaxSummary.pax}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Revenue Summary */}
          <div style={card}>
            <SectionTitle title="Revenue Summary" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Gross Revenue', value: fmtAmount(report.gross), color: NAVY },
                { label: 'TAC at source', value: fmtAmount(report.totalTAC), color: '#92400e' },
                { label: 'Net Collection', value: fmtAmount(report.netCollection), color: '#166534' },
                { label: 'Balance (unpaid)', value: fmtAmount(report.unpaidRemaining), color: report.unpaidRemaining > 0.5 ? '#dc2626' : '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'var(--color-sunken)', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color, wordBreak: 'break-all' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Two rolling subtractions — Gross to Net Collection, then down to the drawer */}
            {(() => {
              const { block1, block2 } = buildRevenueBlocks(report);
              return (
                <div style={{ display: 'grid', gap: 14, maxWidth: 640 }}>
                  <RevenueBlockTable title="Gross Revenue to Net Collection" rows={block1} />
                  <RevenueBlockTable title="Net Collection to Cash in Hand" rows={block2} resultTone={report.cashInHand >= 0 ? '#166534' : '#dc2626'} />
                </div>
              );
            })()}
          </div>

          {/* Refunds */}
          {report.refundedBookings.length > 0 && (
            <div style={card}>
              <SectionTitle title="Refunds" />
              <div style={tableWrap}>
                <table className="data-table" style={tblStyle}>
                  <thead>
                    <tr>
                      {['Booking', 'Date', 'Mode', 'Amount'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.refundedBookings.map((b, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{b.booking_name}</td>
                        <td style={tdStyle}>{b.refund_date || b.date}</td>
                        <td style={tdStyle}>{b.refund_mode || '—'}</td>
                        <td style={{ ...tdNumStyle, color: '#dc2626', fontWeight: 700 }}>−{fmtAmount(b.refund_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12, color: '#dc2626' }}>TOTAL REFUNDS</td>
                      <td style={{ ...tfootStyle, color: '#dc2626' }}>−{fmtAmount(report.totalRefunds)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Partner Report — one table per cruise group, combined TAC at the foot */}
          <div style={card}>
            <SectionTitle title="Partner Report" />
            {report.partnerSunset.length + report.partnerDinner.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-faint)', textAlign: 'center', padding: '16px 0' }}>No partner bookings for this date</div>
            ) : (
              <>
                <PartnerTable title="Sunset & Sundown" rows={report.partnerSunset} />
                <PartnerTable title="Dinner" rows={report.partnerDinner} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 4, padding: '10px 12px', background: 'var(--color-sunken)', borderRadius: 8 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY }}>
                    Total TAC at source, all cruises
                    <span style={{ marginLeft: 8, fontWeight: 500, color: 'var(--color-ink-muted)' }}>
                      {Math.abs(report.partnerTACTotal - report.totalTAC) < 0.5
                        ? '— matches the revenue block'
                        : `— revenue block shows ${fmtAmount(report.totalTAC)}; the difference sits on bookings without a partner name`}
                    </span>
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#92400e', fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(report.partnerTACTotal)}</span>
                </div>
              </>
            )}
          </div>

          {/* Bank Transfer Details */}
          <div style={card}>
            <SectionTitle title="Bank Transfer Details" />
            {report.bankTransfers.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-faint)', textAlign: 'center', padding: '16px 0' }}>No bank transfers for this date</div>
            ) : (
              <div style={tableWrap}>
                <table className="data-table" style={tblStyle}>
                  <thead>
                    <tr>
                      {['Date', 'Name', 'Partner', 'Cruise', 'Pax', 'Amount', 'Notes'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: ['Pax','Amount'].includes(h) ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.bankTransfers.map((b, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{b.date}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{b.booking_name}</td>
                        <td style={{ ...tdStyle, color: '#6b7280' }}>{b.partner_name || '—'}</td>
                        <td style={tdStyle}>{b.cruise_type}</td>
                        <td style={tdNumStyle}>{(b.adults||0)+(b.children||0)}</td>
                        <td style={tdNumStyle}>{fmtAmount(Number(b.bank_transfer_amount) || 0)}</td>
                        <td style={{ ...tdStyle, color: '#6b7280', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={4}>TOTAL</td>
                      <td style={tfootStyle}>{report.bankTransfers.reduce((s,b)=>s+(b.adults||0)+(b.children||0),0)}</td>
                      <td style={tfootStyle}>{fmtAmount(report.bankTransfers.reduce((s,b)=>s+(Number(b.bank_transfer_amount)||0),0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Daily Adjustments — always visible */}
      <div style={card}>
        <SectionTitle title="Daily Adjustments" />
        <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 14 }}>
          Record ad-hoc income or expenses. Positive = income, Negative = expense.
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14, padding: '14px 16px', background: 'var(--color-sunken)', borderRadius: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 }}>Date</label>
            <input type="date" value={adjForm.date} onChange={e => setAdjForm(f => ({ ...f, date: e.target.value }))}
              style={{ background: '#fff', border: '1.5px solid var(--color-border-strong)', borderRadius: 7, padding: '7px 10px', color: NAVY, fontSize: '0.85rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }} />
          </div>
          <div style={{ flex: 2, minWidth: 160 }}>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 }}>Description</label>
            <input type="text" value={adjForm.description} onChange={e => setAdjForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Fuel reimbursement"
              style={{ width: '100%', boxSizing: 'border-box', background: '#fff', border: '1.5px solid var(--color-border-strong)', borderRadius: 7, padding: '7px 10px', color: NAVY, fontSize: '0.85rem', outline: 'none' }} />
          </div>
          <div style={{ minWidth: 100 }}>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 }}>Amount (₹)</label>
            <input type="number" value={adjForm.amount} onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="-500 or 1000"
              style={{ width: '100%', boxSizing: 'border-box', background: '#fff', border: '1.5px solid var(--color-border-strong)', borderRadius: 7, padding: '7px 10px', color: NAVY, fontSize: '0.85rem', outline: 'none' }} />
          </div>
          <button onClick={addAdjustment}
            style={{ padding: '8px 18px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add
          </button>
        </div>
        {adjError && <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600, marginBottom: 10 }}>{adjError}</div>}

        {adjustments.length === 0 ? (
          <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-faint)', textAlign: 'center', padding: '12px 0' }}>No adjustments for this date range.</div>
        ) : (
          <div style={tableWrap}>
            <table className="data-table" style={tblStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Description</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  <th style={{ ...thStyle, width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{a.date}</td>
                    <td style={tdStyle}>{a.description}</td>
                    <td style={{ ...tdNumStyle, color: Number(a.amount) >= 0 ? '#166534' : '#dc2626', fontWeight: 700 }}>
                      {Number(a.amount) >= 0 ? '+' : ''}{fmtAmount(Number(a.amount))}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button onClick={() => deleteAdjustment(a.id)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, padding: '2px 6px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={2}>NET</td>
                  <td style={{ ...tfootStyle, color: adjustments.reduce((s,a)=>s+Number(a.amount),0) >= 0 ? '#166534' : '#dc2626' }}>
                    {fmtAmount(adjustments.reduce((s,a)=>s+Number(a.amount),0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Balance (unpaid) — this report's dates, real-time */}
      {balanceBookings.length > 0 && (
        <div style={card}>
          <SectionTitle title="Balance (unpaid)" />
          <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 14 }}>
            Bookings on {effectiveFrom === effectiveTo ? 'this date' : 'these dates'} with an unpaid remainder — the Balance (unpaid) line above, itemised. Click a row to open in Daily Tracker.
          </p>
          <div style={tableWrap}>
            <table className="data-table" style={tblStyle}>
              <thead>
                <tr>
                  {['Date', 'Booking', 'Cruise', 'Deck', 'Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'].map(h => (
                    <th key={h} style={{ ...thStyle, textAlign: ['Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'].includes(h) ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {balanceBookings.map((b) => {
                  const daysPending = balanceDaysPending(b.date);
                  return (
                    <tr key={b.id}
                      onClick={() => onNavigate('daily-tracker', b.date, { cruise_type: b.cruise_type })}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{b.date}</td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{b.booking_name}</div>
                        {/* Partner line only when there is one — direct guests get no placeholder */}
                        {b.partner_name && <div style={{ fontSize: '0.74rem', fontWeight: 600, color: '#1e40af', marginTop: 1 }}>{b.partner_name}</div>}
                      </td>
                      <td style={tdStyle}>{b.cruise_type}</td>
                      <td style={tdStyle}>{b.deck || '—'}</td>
                      <td style={tdNumStyle}>{b.adults || 0}</td>
                      <td style={tdNumStyle}>{fmtAmount(b.net)}</td>
                      <td style={{ ...tdNumStyle, color: b.paid ? '#166534' : 'var(--color-ink-faint)' }}>{b.paid ? fmtAmount(b.paid) : ''}</td>
                      <td style={{ ...tdNumStyle, color: '#1e40af', fontWeight: 700 }}>{fmtAmount(b.remaining)}</td>
                      <td style={{ ...tdNumStyle, color: daysPending > 2 ? '#dc2626' : '#92400e' }}>{balanceDaysLabel(daysPending)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={7}>TOTAL UNPAID</td>
                  <td style={{ ...tfootStyle, color: '#1e40af' }}>{fmtAmount(balanceBookings.reduce((s, b) => s + b.remaining, 0))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
